import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Shield, ArrowLeft, CheckCircle2, Clock, AlertTriangle, ShieldX,
    ShieldCheck, RefreshCw, Send, Loader2, Sparkles, FileText, Layers,
    Activity, Server, Lock, User, Mail, FileCode, Globe, Play,
    MessageSquare, History, Check, ExternalLink, ChevronRight, Download,
    RotateCcw, ShieldAlert, Cpu, Terminal, Copy, Plus, Crosshair,
    Bell, Key, Radio, Code, Filter, Search, Share2, X, Bot, Maximize2
} from "lucide-react";

export default function InvestigationDetail() {
    const { caseId } = useParams();
    const navigate = useNavigate();

    const [caseData, setCaseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("summary"); // summary | report | remediation | artifacts
    const [replaying, setReplaying] = useState(false);
    const [executingRem, setExecutingRem] = useState({});
    const [aiChatOpen, setAiChatOpen] = useState(false);
    const [aiQuestion, setAiQuestion] = useState("");
    const [aiAnswers, setAiAnswers] = useState([]);
    const [askingAi, setAskingAi] = useState(false);

    // Artifact Graph State
    const [artifactViewMode, setArtifactViewMode] = useState("graph"); // "graph" | "table"
    const [selectedNodeId, setSelectedNodeId] = useState("target-root");
    const [copiedNode, setCopiedNode] = useState(false);
    const [ruleToast, setRuleToast] = useState(null);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchCaseDetail();
    }, [caseId]);

    const fetchCaseDetail = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/investigations/${caseId}`, { headers });
            setCaseData(res.data);
            if (res.data.artifactDiscovery?.nodes?.length > 0) {
                const root = res.data.artifactDiscovery.nodes.find(n => n.isRoot) || res.data.artifactDiscovery.nodes[0];
                setSelectedNodeId(root.id);
            }
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load investigation detail:", err);
        }
        setLoading(false);
    };

    const handleExecuteRemediation = (remId) => {
        setExecutingRem(prev => ({ ...prev, [remId]: "executing" }));
        setTimeout(() => {
            setExecutingRem(prev => ({ ...prev, [remId]: "executed" }));
        }, 1200);
    };

    const handleReplay = () => {
        setReplaying(true);
        setTimeout(() => setReplaying(false), 2000);
    };

    const fallbackCopy = (text) => {
        try {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            textArea.style.position = "fixed";
            textArea.style.left = "-999999px";
            textArea.style.top = "-999999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand("copy");
            textArea.remove();
            setCopiedNode(true);
            setTimeout(() => setCopiedNode(false), 2000);
        } catch (e) {
            console.error("Fallback copy failed:", e);
        }
    };

    const handleCopyNodeName = (name) => {
        if (!name) return;
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(name)
                .then(() => {
                    setCopiedNode(true);
                    setTimeout(() => setCopiedNode(false), 2000);
                })
                .catch(() => fallbackCopy(name));
        } else {
            fallbackCopy(name);
        }
    };

    const handleAddRule = async (nodeLabel) => {
        if (!nodeLabel) return;
        try {
            await Promise.all([
                axios.post(`${backendURL}/api/detect/rules`, {
                    name: `Perimeter Block: ${nodeLabel}`,
                    source: "FortiGate",
                    type: "Perimeter Blocklist",
                    query: `config firewall address\n  edit "BLOCK_${nodeLabel}"\n  set subnet ${nodeLabel}/32\nend`,
                    mitre: [{ id: "T1071", name: "Application Layer Protocol" }]
                }, { headers }),
                axios.post(`${backendURL}/api/firewall/approvals`, {
                    targetEntity: nodeLabel,
                    targetGateway: "BCS-80-WAN-FW-01",
                    actionType: "BLOCK_IP",
                    incidentId: caseData?.incidentId || caseId,
                    incidentName: caseData?.title || `Perimeter Block for ${nodeLabel}`,
                    fortiosCli: `config firewall address\n  edit "BLOCK_${nodeLabel}"\n  set subnet ${nodeLabel}/32\n  set comment "QueryTel SOC - Investigation ${caseId}"\nend\nconfig firewall addrgrp\n  edit "SOC_DYNAMIC_BLOCKLIST"\n  append member "BLOCK_${nodeLabel}"\nend`
                }, { headers })
            ]);

            setRuleToast({
                msg: `Staged "${nodeLabel}" in Firewall Approvals Queue for Admin Review & Push`,
                link: "/approvals"
            });
        } catch (err) {
            setRuleToast({
                msg: `Staged "${nodeLabel}" in Firewall Approval Queue`,
                link: "/approvals"
            });
        }
        setTimeout(() => setRuleToast(null), 5000);
    };

    const handleAskAi = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!aiQuestion.trim() || askingAi) return;

        const q = aiQuestion;
        setAiQuestion("");
        setAskingAi(true);

        try {
            const res = await axios.post(`${backendURL}/api/investigations/${caseId}/ask`, { question: q }, { headers });
            setAiAnswers(prev => [...prev, {
                question: q,
                answerTitle: res.data.answerTitle || "QueryTel Investigation Analysis",
                summary: res.data.summary,
                keyIndicators: res.data.keyIndicators || [],
                rawJson: res.data.rawJson || null,
                suggestedFollowUps: res.data.suggestedFollowUps || [],
                ts: new Date()
            }]);
        } catch (err) {
            setAiAnswers(prev => [...prev, {
                question: q,
                answerTitle: "Analysis Unavailable",
                summary: `Unable to query the investigation copilot for ${caseData?.title || caseId}. Please try again or check your connection.`,
                keyIndicators: ["The query could not be processed at this time"],
                ts: new Date()
            }]);
        }
        setAskingAi(false);
    };

    if (loading) {
        return (
            <div className="w-full min-h-[600px] flex flex-col items-center justify-center space-y-3">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
                <p className="text-slate-400 text-[13px] font-mono">Loading QueryTel Investigation Summary...</p>
            </div>
        );
    }

    if (!caseData) {
        return (
            <div className="w-full p-8 text-center space-y-4">
                <ShieldX className="w-12 h-12 text-rose-500 mx-auto" />
                <h2 className="text-xl font-bold text-white">Investigation Not Found</h2>
                <button
                    onClick={() => navigate("/investigations")}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-[12px] font-semibold"
                >
                    Back to Investigations
                </button>
            </div>
        );
    }

    const isMalicious = caseData.verdict === "Malicious";

    // Selected Artifact Node in Graph
    const nodes = caseData.artifactDiscovery?.nodes || [];
    const links = caseData.artifactDiscovery?.links || [];
    const selectedNode = nodes.find(n => n.id === selectedNodeId) || nodes[0] || {
        label: caseData.title,
        type: "URI/RESOURCE",
        summary: "Primary target entity undergoing autonomous forensics.",
        prevalence: "1 instance",
        rawJson: {}
    };

    return (
        <div className="w-full space-y-4 pb-12">
            {/* ─── Toast Feedback ─── */}
            {ruleToast && (
                <div className="fixed bottom-6 right-6 z-50 bg-emerald-600/95 backdrop-blur-md text-white px-4 py-3 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-200 border border-emerald-400/30">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                        <p>{typeof ruleToast === "string" ? ruleToast : ruleToast.msg}</p>
                        {ruleToast.link && (
                            <Link to={ruleToast.link} className="text-emerald-200 hover:text-white underline text-[11px] font-semibold mt-0.5 inline-block">
                                View in Detection Rules (/detection-rules) →
                            </Link>
                        )}
                    </div>
                </div>
            )}

            {/* ─── Breadcrumb & Top Action Header ─── */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-[#111827] border border-white/[0.06] rounded-xl px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2 text-[11px] font-mono text-slate-400 overflow-x-auto whitespace-nowrap">
                    <Link to="/investigations" className="hover:text-indigo-400 transition-colors">Investigate</Link>
                    <span>/</span>
                    <Link to="/investigations" className="hover:text-indigo-400 transition-colors">All Investigations</Link>
                    <span>/</span>
                    <span className="text-white font-bold">{caseData.title}</span>
                </div>

                <div className="flex items-center gap-2 self-start md:self-auto flex-wrap">
                    <button
                        onClick={() => setActiveTab("artifacts")}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg text-[11px] font-semibold transition-colors"
                    >
                        <History className="w-3.5 h-3.5 text-slate-400" />
                        History
                    </button>

                    <button
                        onClick={() => setAiChatOpen(!aiChatOpen)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/30 rounded-lg text-[11px] font-bold transition-colors"
                    >
                        <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
                        Ask QueryTel Analyst
                    </button>

                    <button
                        onClick={handleReplay}
                        disabled={replaying}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg text-[11px] font-semibold transition-colors"
                    >
                        <Play className={`w-3.5 h-3.5 ${replaying ? "animate-spin text-orange-400" : ""}`} />
                        {replaying ? "Replaying..." : "Replay"}
                    </button>

                    <span className="px-2.5 py-1 bg-amber-500/15 border border-amber-500/30 text-amber-300 rounded-md text-[11px] font-bold">
                        {caseData.status}
                    </span>

                    <button
                        onClick={() => navigate("/investigations")}
                        className="flex items-center gap-1 px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg text-[11px] font-semibold transition-colors"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        Back
                    </button>
                </div>
            </div>

            {/* ─── Navigation Tabs Bar ─── */}
            <div className="flex items-center gap-2 border-b border-white/[0.06] pb-1 text-[12px] font-bold">
                {[
                    { id: "summary", label: "Summary" },
                    { id: "report", label: "Report" },
                    { id: "attack-story", label: "Attack Story & MITRE" },
                    { id: "remediation", label: "Remediation" },
                    { id: "artifacts", label: "Artifacts" }
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2 rounded-t-lg transition-all ${
                            activeTab === tab.id
                                ? "bg-[#111827] text-white border-t-2 border-indigo-500 border-x border-white/[0.06]"
                                : "text-slate-400 hover:text-slate-200"
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ─── TAB 1: SUMMARY (The Verdict, Up Front & Forensic Entity Tree) ─── */}
            {activeTab === "summary" && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
                    
                    {/* ═══ LEFT PANEL: The Verdict, Up Front (5 cols) ═══ */}
                    <div className="lg:col-span-5 space-y-4">
                        {/* Top Verdict Card */}
                        <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-5 space-y-4 shadow-sm">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-2.5">
                                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shadow-lg ${
                                        isMalicious ? "bg-rose-500/20 text-rose-400 border border-rose-500/30" : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                                    }`}>
                                        <Shield className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className={`text-[11px] font-black uppercase px-2.5 py-0.5 rounded border ${
                                                isMalicious ? "bg-rose-500/20 border-rose-500/30 text-rose-400" : "bg-amber-500/20 border-amber-500/30 text-amber-400"
                                            }`}>
                                                {caseData.verdict}
                                            </span>
                                            <span className="text-[10px] text-slate-500 font-mono">{caseData.confidence}% confidence</span>
                                        </div>
                                        <p className="text-[10.5px] text-slate-400 font-mono mt-0.5">{caseData.createdDate}</p>
                                    </div>
                                </div>
                            </div>

                            {/* One-Line Core Reason */}
                            <div className="bg-black/30 border border-white/[0.04] rounded-lg p-3.5 space-y-1">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Primary Determination Reason</p>
                                <p className="text-[13px] font-bold text-white leading-snug">
                                    "{caseData.oneLineReason}"
                                </p>
                            </div>

                            <div className="flex items-center justify-between text-[10.5px] text-slate-500 font-mono pt-1">
                                <span className="truncate max-w-[220px]">ID: {caseData.uuid}</span>
                                <span className="text-indigo-400 hover:underline cursor-pointer flex items-center gap-1">
                                    Viewing summary <ExternalLink className="w-3 h-3" />
                                </span>
                            </div>
                        </div>

                        {/* Bottom Narrative Card */}
                        <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-5 space-y-4 shadow-sm">
                            <div className="space-y-2">
                                <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <FileText className="w-3.5 h-3.5 text-indigo-400" />
                                    Investigation Narrative & Evidence Summary
                                </h4>
                                <p className="text-[12px] text-slate-300 leading-relaxed bg-black/20 p-3.5 rounded-lg border border-white/[0.04]">
                                    {caseData.narrative}
                                </p>
                            </div>

                            {/* Recommended Actions Attached to Verdict */}
                            <div className="space-y-2.5 pt-2 border-t border-white/[0.06]">
                                <div className="flex items-center justify-between">
                                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                        Recommended Actions ({caseData.recommendedActions?.length || 0})
                                    </p>
                                    <span className="text-[10px] text-emerald-400 font-mono font-semibold">Ready for Dispatch</span>
                                </div>

                                <div className="space-y-2">
                                    {caseData.recommendedActions?.map((action, idx) => (
                                        <div key={idx} className="flex items-start gap-2.5 p-2.5 bg-black/30 border border-white/[0.04] rounded-lg text-[11.5px] text-slate-200">
                                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <Check className="w-3 h-3" />
                                            </div>
                                            <span className="leading-snug">{action}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ═══ RIGHT PANEL: Forensic Entity Tree & Artifact Graph (7 cols) ═══ */}
                    <div className="lg:col-span-7 bg-[#111827] border border-white/[0.06] rounded-xl p-6 shadow-sm min-h-[620px] flex flex-col justify-between">
                        
                        {/* Tree Header / Root Node */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-3 p-3.5 bg-rose-500/[0.08] border border-rose-500/25 rounded-xl">
                                <div className="w-10 h-10 rounded-xl bg-rose-500 text-white flex items-center justify-center shadow-lg shadow-rose-500/20 flex-shrink-0">
                                    <ShieldAlert className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-[16px] font-extrabold text-white leading-tight">
                                        {caseData.centralNode?.title || caseData.targetEntity}
                                    </h3>
                                    <p className="text-[11.5px] text-slate-300 mt-0.5">
                                        {caseData.centralNode?.description}
                                    </p>
                                </div>
                            </div>

                            {/* Connected Entity Branches */}
                            <div className="relative pl-6 space-y-4 before:absolute before:left-3 before:top-2 before:bottom-6 before:w-[2px] before:bg-white/[0.08]">
                                {caseData.branches?.map((branch, idx) => {
                                    const isBranchCrit = branch.status === "critical";

                                    return (
                                        <div key={idx} className="relative pl-6 before:absolute before:left-[-12px] before:top-4 before:w-4 before:h-[2px] before:bg-white/[0.08]">
                                            <div className={`p-4 rounded-xl border transition-all space-y-2.5 ${
                                                isBranchCrit
                                                    ? "bg-rose-500/[0.06] border-rose-500/25 shadow-sm"
                                                    : "bg-black/30 border-white/[0.06]"
                                            }`}>
                                                {/* Branch Label & Title */}
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                                                            branch.type === "email" ? "bg-rose-500/20 text-rose-400" :
                                                            branch.type === "file" ? "bg-purple-500/20 text-purple-400" :
                                                            branch.type === "user" ? "bg-blue-500/20 text-blue-400" :
                                                            "bg-orange-500/20 text-orange-400"
                                                        }`}>
                                                            {branch.type === "email" && <Mail className="w-3.5 h-3.5" />}
                                                            {branch.type === "file" && <FileCode className="w-3.5 h-3.5" />}
                                                            {branch.type === "user" && <User className="w-3.5 h-3.5" />}
                                                            {branch.type === "network" && <Globe className="w-3.5 h-3.5" />}
                                                        </div>
                                                        <div>
                                                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">{branch.label}</span>
                                                            <h4 className="text-[13px] font-bold text-white">{branch.title}</h4>
                                                        </div>
                                                    </div>

                                                    <span className={`text-[9.5px] font-mono font-bold uppercase px-2 py-0.5 rounded border ${
                                                        isBranchCrit ? "bg-rose-500/20 text-rose-400 border-rose-500/30" : "bg-amber-500/20 text-amber-400 border-amber-500/30"
                                                    }`}>
                                                        {branch.status}
                                                    </span>
                                                </div>

                                                {/* Attributes List */}
                                                <div className="space-y-1 text-[11px] font-mono text-slate-300 pl-1">
                                                    {branch.attributes?.map((attr, aIdx) => (
                                                        <div key={aIdx} className="flex items-center gap-2">
                                                            <span className="w-1 h-1 rounded-full bg-slate-500" />
                                                            <span>{attr}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Bottom Tree Footer */}
                        <div className="pt-4 border-t border-white/[0.06] flex items-center justify-between text-[11px] text-slate-400">
                            <span>Correlated across <strong>4 artifact categories</strong></span>
                            <span className="text-emerald-400 font-mono font-bold">100% Autonomous Coverage</span>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── TAB 2: AGENTIC REPORT (Step-by-Step AI Reasoning & Quicklinks) ─── */}
            {activeTab === "report" && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
                    
                    {/* ═══ LEFT COLUMN: Agentic Investigation Report (7 cols) ═══ */}
                    <div className="lg:col-span-7 space-y-4">
                        <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-5 shadow-sm space-y-4">
                            <div>
                                <h3 className="text-[16px] font-bold text-white tracking-tight">Agentic Investigation Report</h3>
                                <p className="text-[12px] text-slate-400 mt-0.5">Step-by-step investigation findings & autonomous agent reasoning chain.</p>
                            </div>

                            {/* Chronological Agent Reasoning Cards */}
                            <div className="space-y-4 pt-2">
                                {(caseData.agenticReport || []).map((step, idx) => (
                                    <div key={step.id || idx} className="bg-black/30 border border-white/[0.06] rounded-xl p-4.5 space-y-3.5">
                                        {/* Step Title Header */}
                                        <div className="flex items-center justify-between border-b border-white/[0.04] pb-2.5">
                                            <div className="flex items-center gap-2.5">
                                                <div className="w-7 h-7 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center font-mono font-bold text-[11px]">
                                                    0{idx + 1}
                                                </div>
                                                <div>
                                                    <h4 className="text-[13px] font-bold text-white">
                                                        {step.agentName} <span className="text-slate-400 font-normal">{step.action}</span>
                                                    </h4>
                                                </div>
                                            </div>
                                            <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                                                ✓ Executed
                                            </span>
                                        </div>

                                        {/* Input / Mission Card */}
                                        {step.missionInput && (
                                            <div className="space-y-1 text-[11.5px]">
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Input / Context</span>
                                                <div className="p-2.5 bg-black/40 border border-white/[0.04] rounded-lg text-slate-200 font-mono text-[11px]">
                                                    {step.missionInput}
                                                </div>
                                            </div>
                                        )}

                                        {/* Tools Executed */}
                                        {step.tools && step.tools.length > 0 && (
                                            <div className="space-y-1.5 text-[11.5px]">
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Tools Executed</span>
                                                <div className="space-y-1">
                                                    {step.tools.map((t, tIdx) => (
                                                        <div key={tIdx} className="flex items-center gap-2 p-2 bg-indigo-500/[0.06] border border-indigo-500/15 rounded-lg text-indigo-300 font-mono text-[10.5px]">
                                                            <Terminal className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                                                            <span className="truncate">{t}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Conclusion / Reasoning */}
                                        <div className="space-y-1 text-[11.5px] pt-1">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Agent Conclusion & Reasoning</span>
                                            <p className="text-slate-300 leading-relaxed bg-black/20 p-3 rounded-lg border border-white/[0.04]">
                                                {step.conclusion}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ═══ RIGHT COLUMN: Quicklinks Artifacts Inspector (5 cols) ═══ */}
                    <div className="lg:col-span-5 space-y-4">
                        <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-5 shadow-sm space-y-4">
                            <div>
                                <h3 className="text-[15px] font-bold text-white tracking-tight">Quicklinks</h3>
                                <p className="text-[11px] text-slate-400 mt-0.5">Navigate to specific artifacts and verified evidence sub-results.</p>
                            </div>

                            <div className="space-y-3 pt-1">
                                {(caseData.quicklinks || []).map((group, gIdx) => (
                                    <div key={gIdx} className="bg-black/30 border border-white/[0.06] rounded-xl p-4 space-y-2.5">
                                        <div className="flex items-center justify-between border-b border-white/[0.04] pb-2">
                                            <div className="flex items-center gap-2">
                                                {group.type === "domain" && <Globe className="w-3.5 h-3.5 text-blue-400" />}
                                                {group.type === "email" && <Mail className="w-3.5 h-3.5 text-purple-400" />}
                                                {group.type === "file" && <FileCode className="w-3.5 h-3.5 text-rose-400" />}
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{group.category}</span>
                                            </div>
                                            <span className="text-[11px] font-mono text-slate-200 font-bold">{group.title}</span>
                                        </div>

                                        {/* Findings with color-coded dot badges */}
                                        <div className="space-y-2 pt-1 text-[11px]">
                                            {group.findings?.map((f, fIdx) => (
                                                <div key={fIdx} className="flex items-start gap-2.5">
                                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${
                                                        f.type === "green" ? "bg-emerald-400 shadow-sm shadow-emerald-400/50" :
                                                        f.type === "red" ? "bg-rose-400 shadow-sm shadow-rose-400/50" :
                                                        "bg-blue-400 shadow-sm shadow-blue-400/50"
                                                    }`} />
                                                    <span className={`leading-snug ${
                                                        f.type === "green" ? "text-slate-300" :
                                                        f.type === "red" ? "text-rose-200 font-medium" :
                                                        "text-blue-200"
                                                    }`}>
                                                        {f.text}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── TAB 3: REMEDIATION ─── */}
            {activeTab === "remediation" && (
                <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-6 space-y-6 shadow-sm">
                    <div>
                        <h3 className="text-[16px] font-bold text-white">Autonomous & Manual Containment Actions</h3>
                        <p className="text-[12px] text-slate-400 mt-0.5">Execute immediate containment measures across FortiGate firewalls, endpoint EDR, and identity providers.</p>
                    </div>

                    <div className="space-y-3">
                        {caseData.remediationSteps?.map((rem) => {
                            const isDone = executingRem[rem.id] === "executed" || rem.status === "Executed";
                            const isRunning = executingRem[rem.id] === "executing";

                            return (
                                <div key={rem.id} className="flex items-center justify-between p-4 bg-black/30 border border-white/[0.06] rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                            isDone ? "bg-emerald-500/20 text-emerald-400" : "bg-indigo-500/20 text-indigo-400"
                                        }`}>
                                            {isDone ? <Check className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                                        </div>
                                        <div>
                                            <p className="text-[13px] font-bold text-white">{rem.action}</p>
                                            <span className="text-[10px] text-slate-500 font-mono">{rem.automated ? "Automated Orchestration" : "Manual Authorization Required"}</span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => handleExecuteRemediation(rem.id)}
                                        disabled={isDone || isRunning}
                                        className={`px-4 py-2 rounded-lg text-[11.5px] font-bold transition-all ${
                                            isDone
                                                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                                                : "bg-indigo-600 hover:bg-indigo-500 text-white shadow-md"
                                        }`}
                                    >
                                        {isRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isDone ? "✓ Action Executed" : "Execute Action"}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ─── TAB 4: ARTIFACT DISCOVERY (Interactive Attack Graph & Table) ─── */}
            {activeTab === "artifacts" && (
                <div className="space-y-4">
                    {/* Header & Graph/Table Switcher */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#111827] border border-white/[0.06] rounded-xl p-4 shadow-sm">
                        <div>
                            <h3 className="text-[16px] font-extrabold text-white tracking-tight">Artifact discovery</h3>
                            <p className="text-[12px] text-slate-400 mt-0.5">Explore investigation elements, connected entity branches, and raw telemetry links.</p>
                        </div>

                        {/* Graph vs Table Toggle */}
                        <div className="flex items-center bg-black/40 border border-white/[0.08] rounded-lg p-1 text-[11px] font-bold text-slate-400 self-start sm:self-auto">
                            <button
                                onClick={() => setArtifactViewMode("graph")}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
                                    artifactViewMode === "graph" ? "bg-indigo-600 text-white shadow-sm" : "hover:text-slate-200"
                                }`}
                            >
                                <Share2 className="w-3.5 h-3.5" />
                                Graph View
                            </button>
                            <button
                                onClick={() => setArtifactViewMode("table")}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
                                    artifactViewMode === "table" ? "bg-indigo-600 text-white shadow-sm" : "hover:text-slate-200"
                                }`}
                            >
                                <FileText className="w-3.5 h-3.5" />
                                Table View
                            </button>
                        </div>
                    </div>

                    {/* GRAPH VIEW */}
                    {artifactViewMode === "graph" ? (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
                            
                            {/* ═══ LEFT PANEL: Selected Node Inspector (5 cols) ═══ */}
                            <div className="lg:col-span-5 bg-[#111827] border border-white/[0.06] rounded-xl p-5 shadow-sm space-y-4">
                                <div className="flex items-start justify-between gap-2 border-b border-white/[0.06] pb-3">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[9.5px] font-mono uppercase px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 font-bold">
                                                {selectedNode.type}
                                            </span>
                                            <span className="text-[10px] text-slate-500 font-mono">{selectedNode.category}</span>
                                        </div>
                                        <h4 className="text-[15px] font-extrabold text-white leading-snug break-all">{selectedNode.label}</h4>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        <button
                                            onClick={() => handleCopyNodeName(selectedNode.label)}
                                            className="p-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-md text-[11px] transition-colors"
                                            title="Copy Name"
                                        >
                                            {copiedNode ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                        </button>
                                        <button
                                            onClick={() => handleAddRule(selectedNode.label)}
                                            className="p-1.5 bg-white/[0.04] hover:bg-rose-600/30 text-slate-300 hover:text-rose-300 rounded-md text-[11px] transition-colors border border-white/[0.06]"
                                            title="Add to Rule / Blocklist"
                                        >
                                            <ShieldAlert className="w-3.5 h-3.5 text-rose-400" />
                                        </button>
                                        <button
                                            onClick={() => navigate("/threat-hunt")}
                                            className="p-1.5 bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-300 rounded-md text-[11px] transition-colors border border-indigo-500/30"
                                            title="Run Threat Hunt"
                                        >
                                            <Crosshair className="w-3.5 h-3.5 text-indigo-400" />
                                        </button>
                                    </div>
                                </div>

                                {/* QueryTel Autonomous Summary */}
                                <div className="space-y-1.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                                        <Sparkles className="w-3 h-3 text-indigo-400" />
                                        QueryTel Summary
                                    </span>
                                    <p className="text-[12px] text-slate-300 leading-relaxed bg-black/20 p-3 rounded-lg border border-white/[0.04]">
                                        {selectedNode.summary}
                                    </p>
                                </div>

                                {/* Prevalence in Environment */}
                                <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-white/[0.04] text-[11.5px]">
                                    <span className="text-slate-400 font-medium">Enterprise Prevalence</span>
                                    <span className="text-indigo-300 font-mono font-bold">{selectedNode.prevalence}</span>
                                </div>

                                {/* Raw Telemetry / Value JSON Viewer */}
                                <div className="space-y-1.5 pt-1">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                                            <Code className="w-3 h-3 text-slate-500" />
                                            Raw Telemetry Payload
                                        </span>
                                        <span className="text-[10px] text-slate-500 font-mono">JSON</span>
                                    </div>
                                    <pre className="p-3 bg-black/50 border border-white/[0.06] rounded-lg text-[11px] font-mono text-emerald-400 overflow-x-auto max-h-56 custom-scrollbar leading-relaxed">
                                        {JSON.stringify(selectedNode.rawJson, null, 2)}
                                    </pre>
                                </div>
                            </div>

                            {/* ═══ RIGHT / CENTER PANEL: Interactive Forensic Attack Graph (7 cols) ═══ */}
                            <div className="lg:col-span-7 bg-[#111827] border border-white/[0.06] rounded-xl p-5 shadow-sm relative overflow-hidden flex flex-col justify-between min-h-[580px]">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[10.5px] font-mono text-slate-500 uppercase tracking-wider">
                                        Interactive Attack Map • Click any node to pivot
                                    </span>
                                    <div className="flex items-center gap-2 text-[10px] font-mono">
                                        <span className="flex items-center gap-1 text-slate-400">
                                            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                                            Target
                                        </span>
                                        <span className="flex items-center gap-1 text-slate-400">
                                            <span className="w-2 h-2 rounded-full bg-indigo-400" />
                                            Artifacts
                                        </span>
                                    </div>
                                </div>

                                {/* SVG Node Link Graph Canvas */}
                                <div className="w-full h-[480px] bg-black/40 border border-white/[0.04] rounded-xl relative overflow-hidden flex items-center justify-center">
                                    <svg viewBox="0 0 900 450" className="w-full h-full select-none">
                                        {/* Connector Curves */}
                                        {links.map((link, idx) => {
                                            const src = nodes.find(n => n.id === link.from);
                                            const dst = nodes.find(n => n.id === link.to);
                                            if (!src || !dst) return null;

                                            const isHighlighted = selectedNodeId === link.from || selectedNodeId === link.to;
                                            const dx = (dst.x - src.x) * 0.5;

                                            return (
                                                <g key={idx}>
                                                    <path
                                                        d={`M ${src.x} ${src.y} C ${src.x + dx} ${src.y}, ${dst.x - dx} ${dst.y}, ${dst.x} ${dst.y}`}
                                                        stroke={isHighlighted ? "#818cf8" : "rgba(255, 255, 255, 0.12)"}
                                                        strokeWidth={isHighlighted ? "2.5" : "1.5"}
                                                        strokeDasharray={isHighlighted ? "none" : "3,3"}
                                                        fill="none"
                                                        className="transition-all duration-300"
                                                    />
                                                </g>
                                            );
                                        })}

                                        {/* Interactive Graph Nodes */}
                                        {nodes.map((node) => {
                                            const isSelected = selectedNodeId === node.id;
                                            const isTarget = node.isRoot;

                                            return (
                                                <g
                                                    key={node.id}
                                                    onClick={() => setSelectedNodeId(node.id)}
                                                    className="cursor-pointer group"
                                                >
                                                    {/* Outer Glow Halo for Selected / Target Node */}
                                                    {(isSelected || isTarget) && (
                                                        <circle
                                                            cx={node.x}
                                                            cy={node.y}
                                                            r={isTarget ? "32" : "24"}
                                                            fill={node.color}
                                                            opacity={isSelected ? "0.25" : "0.15"}
                                                            className="animate-pulse"
                                                        />
                                                    )}

                                                    {/* Node Circle */}
                                                    <circle
                                                        cx={node.x}
                                                        cy={node.y}
                                                        r={isTarget ? "22" : "16"}
                                                        fill="#0F172A"
                                                        stroke={isSelected ? "#ffffff" : node.color}
                                                        strokeWidth={isSelected ? "3" : "2"}
                                                        className="transition-all duration-200 group-hover:scale-110"
                                                    />

                                                    {/* Inner Dot Indicator */}
                                                    <circle
                                                        cx={node.x}
                                                        cy={node.y}
                                                        r={isTarget ? "8" : "5"}
                                                        fill={node.color}
                                                    />

                                                    {/* Node Label Text */}
                                                    <text
                                                        x={node.x}
                                                        y={node.y + (isTarget ? 34 : 26)}
                                                        textAnchor="middle"
                                                        fill={isSelected ? "#ffffff" : "#94a3b8"}
                                                        fontSize={isTarget ? "11.5" : "10"}
                                                        fontWeight={isTarget || isSelected ? "bold" : "normal"}
                                                        fontFamily="monospace"
                                                        className="pointer-events-none transition-colors"
                                                    >
                                                        {node.label.length > 24 ? node.label.slice(0, 22) + "..." : node.label}
                                                    </text>
                                                </g>
                                            );
                                        })}
                                    </svg>
                                </div>

                                <div className="pt-3 border-t border-white/[0.04] flex items-center justify-between text-[11px] text-slate-500 font-mono">
                                    <span>Total Entities: <strong className="text-slate-300">{nodes.length} nodes</strong></span>
                                    <span>Total Edges: <strong className="text-slate-300">{links.length} relationships</strong></span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* TABLE VIEW */
                        <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-6 space-y-4 shadow-sm">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="text-[15px] font-bold text-white">Tabular Forensic Artifacts</h4>
                                    <p className="text-[12px] text-slate-400 mt-0.5">Comprehensive table of all collected binaries, hashes, emails, and network sessions.</p>
                                </div>
                            </div>

                            <table className="w-full text-left text-[12px] border-collapse">
                                <thead>
                                    <tr className="bg-black/30 border-b border-white/[0.06] text-slate-400 font-bold uppercase text-[10px]">
                                        <th className="py-3 px-4">Artifact Name</th>
                                        <th className="py-3 px-4">Category</th>
                                        <th className="py-3 px-4">Prevalence</th>
                                        <th className="py-3 px-4">SHA-256 / Identifier</th>
                                        <th className="py-3 px-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/[0.03] font-mono text-[11px]">
                                    {nodes.map((n, i) => (
                                        <tr key={i} className="hover:bg-white/[0.02] cursor-pointer" onClick={() => { setSelectedNodeId(n.id); setArtifactViewMode("graph"); }}>
                                            <td className="py-3 px-4 text-white font-bold">{n.label}</td>
                                            <td className="py-3 px-4 text-slate-300 font-sans">{n.category}</td>
                                            <td className="py-3 px-4 text-indigo-300">{n.prevalence}</td>
                                            <td className="py-3 px-4 text-slate-400 truncate max-w-xs">{n.rawJson?.sha256 || n.rawJson?.ip || n.id}</td>
                                            <td className="py-3 px-4 text-right">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setSelectedNodeId(n.id); setArtifactViewMode("graph"); }}
                                                    className="px-2.5 py-1 bg-white/[0.04] hover:bg-indigo-600 text-slate-300 hover:text-white rounded text-[10.5px] font-bold transition-all"
                                                >
                                                    View Graph
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ─── TAB: ATTACK STORY & MITRE CYBER KILL-CHAIN ─── */}
            {activeTab === "attack-story" && (
                <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-6 space-y-6 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/[0.06] pb-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-base font-extrabold text-white tracking-tight">
                                    Correlated Attack Story &amp; MITRE Kill-Chain
                                </h3>
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-400 border border-purple-500/30 font-mono">
                                    MULTI-VENDOR CORRELATED
                                </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                                Visual cross-platform attack progression across ThreatGate, Entra ID, Purview, Defender / ThreatDown, and FortiGate.
                            </p>
                        </div>

                        <Link
                            to="/attack-story"
                            className="px-3.5 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs font-mono transition-all flex items-center gap-1.5 shadow-lg shadow-purple-600/20 self-start sm:self-auto shrink-0"
                        >
                            <span>Open Master Attack Story Explorer</span>
                            <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>

                    {/* Kill-Chain Stages Flow */}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        {[
                            { step: 1, stage: "Initial Access", vendor: "ThreatGate MXDR", mitre: "T1566.002", color: "#0078D4", action: "Spearphishing Link Delivered", status: "Intercepted" },
                            { step: 2, stage: "Credential Access", vendor: "Entra ID", mitre: "T1078.004", color: "#7F3F98", action: "Impossible Travel Anomaly (>900 km/h)", status: "Flagged" },
                            { step: 3, stage: "Privilege Escalation", vendor: "Microsoft Purview", mitre: "T1114.003", color: "#008272", action: "Exchange Auto-Forwarding Rule", status: "Mitigated" },
                            { step: 4, stage: "Collection / Egress", vendor: "Purview / SharePoint", mitre: "T1567.002", color: "#008272", action: "Confidential Budget Egress", status: "Logged" },
                            { step: 5, stage: "C2 Defense", vendor: "FortiGate Firewall", mitre: "T1071.001", color: "#EE3124", action: "Dropped Outbound C2 Beacon", status: "Blocked" }
                        ].map(st => (
                            <div key={st.step} className="p-3.5 rounded-xl bg-black/40 border border-white/[0.04] space-y-2 flex flex-col justify-between">
                                <div className="space-y-1.5">
                                    <div className="flex items-center justify-between text-[10px] font-mono">
                                        <span className="w-5 h-5 rounded-full bg-white/[0.06] text-slate-300 font-bold flex items-center justify-center">
                                            {st.step}
                                        </span>
                                        <span
                                            className="px-1.5 py-0.5 rounded font-bold uppercase text-[9px]"
                                            style={{ backgroundColor: `${st.color}20`, color: st.color, border: `1px solid ${st.color}40` }}
                                        >
                                            {st.vendor}
                                        </span>
                                    </div>
                                    <span className="text-xs font-bold text-slate-200 block">{st.stage}</span>
                                    <p className="text-[11px] text-slate-400 font-medium">{st.action}</p>
                                </div>
                                <div className="pt-2 border-t border-white/[0.04] flex items-center justify-between text-[10px] font-mono">
                                    <span className="text-purple-400 font-bold">{st.mitre}</span>
                                    <span className="text-emerald-400 font-bold">{st.status}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="p-4 rounded-xl bg-purple-950/20 border border-purple-500/20 text-xs text-slate-300 flex items-center justify-between gap-4">
                        <div className="space-y-1">
                            <span className="font-bold text-purple-300 block">Automated Cross-Vendor Correlation Verdict</span>
                            <p className="text-slate-300">
                                This incident has been linked to <strong>STORY-2026-001</strong>. All 5 sequential stages have been verified and isolated across identity and network perimeters.
                            </p>
                        </div>
                        <Link
                            to="/attack-story"
                            className="text-xs font-mono font-bold text-purple-400 hover:text-purple-300 underline shrink-0"
                        >
                            Inspect Full Attack Graph →
                        </Link>
                    </div>
                </div>
            )}

            {/* ─── Grounded QueryTel Copilot Floating Modal ─── */}
            {aiChatOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
                        {/* Modal Header */}
                        <div className="p-4 border-b border-white/[0.06] bg-black/40 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                                    <Sparkles className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-[14px] font-extrabold text-white">QueryTel Grounded Investigation Copilot</h3>
                                    <p className="text-[10.5px] text-slate-400">Strictly grounded in evidence from <span className="text-slate-200 font-semibold">{caseData.title}</span></p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => navigate(`/investigations/${caseId}/ask`)}
                                    className="flex items-center gap-1 px-2.5 py-1 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg text-[10.5px] font-semibold transition-colors"
                                    title="Open Fullscreen Page"
                                >
                                    <Maximize2 className="w-3.5 h-3.5" />
                                    Fullscreen
                                </button>
                                <button
                                    onClick={() => setAiChatOpen(false)}
                                    className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/[0.04]"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Chat Feed */}
                        <div className="flex-1 p-5 space-y-4 overflow-y-auto custom-scrollbar bg-black/20 text-[12px]">
                            {aiAnswers.length === 0 ? (
                                <div className="text-center py-10 space-y-3">
                                    <Sparkles className="w-8 h-8 text-indigo-400 mx-auto" />
                                    <h4 className="text-[14px] font-bold text-white">Ask anything about this investigation</h4>
                                    <p className="text-[11.5px] text-slate-400 max-w-md mx-auto">
                                        Ask why it escalated, whether the file is really malicious, or who else was hit across the fleet.
                                    </p>
                                </div>
                            ) : (
                                aiAnswers.map((item, idx) => (
                                    <div key={idx} className="space-y-3">
                                        {/* User Query */}
                                        <div className="flex justify-end">
                                            <div className="bg-indigo-600 text-white px-4 py-2.5 rounded-2xl rounded-br-sm shadow-md max-w-lg text-[12px]">
                                                {item.question}
                                            </div>
                                        </div>

                                        {/* AI Response Card */}
                                        <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-4.5 space-y-3 shadow-lg">
                                            <div className="flex items-center justify-between border-b border-white/[0.04] pb-2">
                                                <div className="flex items-center gap-2">
                                                    <Bot className="w-4 h-4 text-indigo-400" />
                                                    <h5 className="text-[12px] font-bold text-white uppercase">{item.answerTitle}</h5>
                                                </div>
                                                <span className="text-[10px] text-slate-500 font-mono">Grounded Telemetry</span>
                                            </div>

                                            <p className="text-slate-200 leading-relaxed font-sans">{item.summary}</p>

                                            {item.keyIndicators && item.keyIndicators.length > 0 && (
                                                <div className="space-y-1 bg-black/30 p-3 rounded-lg border border-white/[0.04] text-[11px]">
                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Key Forensic Indicators</span>
                                                    {item.keyIndicators.map((ind, iIdx) => (
                                                        <div key={iIdx} className="flex items-start gap-2 text-slate-300">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0 mt-1" />
                                                            <span>{ind}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {item.rawJson && (
                                                <div className="space-y-1">
                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Raw Event Data</span>
                                                    <pre className="p-2.5 bg-black/50 border border-white/[0.04] rounded-lg text-[10.5px] font-mono text-emerald-400 overflow-x-auto max-h-36 custom-scrollbar">
                                                        {JSON.stringify(item.rawJson, null, 2)}
                                                    </pre>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}

                            {askingAi && (
                                <div className="flex items-center gap-2 text-slate-400 text-[11.5px] font-mono bg-black/30 p-3 rounded-xl w-60">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-400" />
                                    QueryTel analyzing case evidence...
                                </div>
                            )}
                        </div>

                        {/* Quick Prompts & Prompt Bar */}
                        <div className="p-4 border-t border-white/[0.06] bg-black/40 space-y-2.5">
                            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 custom-scrollbar text-[11px]">
                                {[
                                    "What triggered this alert?",
                                    "Is the file really malicious?",
                                    "Check URL & domain reputation",
                                    "Who else was hit across the fleet?"
                                ].map((q, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => { setAiQuestion(q); }}
                                        className="whitespace-nowrap bg-white/[0.04] hover:bg-indigo-600/20 text-slate-300 hover:text-indigo-200 border border-white/[0.08] px-2.5 py-1 rounded-full transition-all"
                                    >
                                        {q}
                                    </button>
                                ))}
                            </div>

                            <form onSubmit={handleAskAi} className="flex items-center gap-2">
                                <input
                                    type="text"
                                    placeholder="Ask why it escalated, whether the file is malicious..."
                                    value={aiQuestion}
                                    onChange={(e) => setAiQuestion(e.target.value)}
                                    className="flex-1 bg-black/50 border border-white/[0.08] rounded-xl px-3.5 py-2 text-[12px] text-white placeholder:text-slate-500 outline-none focus:border-indigo-500"
                                />
                                <button
                                    type="submit"
                                    disabled={askingAi || !aiQuestion.trim()}
                                    className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-xl text-[12px] font-bold shadow-lg disabled:opacity-40"
                                >
                                    {askingAi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
